import { ChevronRight, LogOut, Moon, PanelLeft, Sun, UserCircle } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { resolvePageTitle } from "@/app/navigation";
import { Button } from "@/components/ui/button";
import { useSidebar } from "@/components/ui/sidebar";
import { useAuth } from "@/features/auth";
import { NotificationBell } from "@/features/notifications/components/NotificationBell";
import { useTheme } from "@/lib/hooks/use-theme";

function getProfileLabel(profile: ReturnType<typeof useAuth>["profile"]) {
  return profile?.full_name || profile?.email || "Operator";
}

/**
 * Sticky, responsive application header. Hosts the mobile sidebar trigger,
 * a route breadcrumb, the notification bell, and the user avatar.
 */
export function TopNav() {
  const { toggleSidebar } = useSidebar();
  const { profile, signOut } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const location = useLocation();

  const pageTitle = resolvePageTitle(location.pathname);
  const isHome = location.pathname === "/";

  return (
    <header className="sticky top-0 z-30 flex h-[var(--shell-top-nav-height)] shrink-0 items-center gap-2 border-border border-b bg-background/80 px-3 backdrop-blur-md supports-[backdrop-filter]:bg-background/65 sm:gap-3 sm:px-4 lg:px-6">
      <Button
        aria-label="Toggle sidebar"
        className="size-[44px] text-muted-foreground sm:size-9"
        onClick={toggleSidebar}
        size="icon"
        type="button"
        variant="ghost"
      >
        <PanelLeft aria-hidden="true" className="size-5" />
      </Button>

      <nav aria-label="Breadcrumb" className="flex min-w-0 flex-1 items-center gap-1.5">
        <Link
          className="hidden shrink-0 rounded text-muted-foreground text-sm transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background sm:inline"
          to="/"
        >
          Home
        </Link>
        {!isHome && (
          <ChevronRight
            aria-hidden="true"
            className="hidden size-4 shrink-0 text-icon-secondary sm:inline"
          />
        )}
        <span className="truncate font-medium text-base text-foreground sm:text-[15px]">
          {pageTitle}
        </span>
      </nav>

      <div className="flex shrink-0 items-center gap-1 sm:gap-2">
        <Button
          aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
          aria-pressed={isDark}
          className="size-[44px] text-muted-foreground sm:size-9"
          onClick={toggleTheme}
          size="icon"
          type="button"
          variant="ghost"
        >
          {isDark ? (
            <Sun aria-hidden="true" className="size-[18px]" />
          ) : (
            <Moon aria-hidden="true" className="size-[18px]" />
          )}
        </Button>
        <NotificationBell />
        <div className="flex items-center gap-0 sm:gap-2 sm:rounded-lg sm:border sm:border-border sm:bg-card sm:px-2 sm:py-1">
          <UserCircle aria-hidden="true" className="hidden size-5 text-icon-secondary sm:block" />
          <span className="hidden max-w-32 truncate font-medium text-foreground text-sm sm:inline">
            {getProfileLabel(profile)}
          </span>
          <Button
            aria-label="Sign out"
            className="size-[44px] text-muted-foreground sm:size-7"
            onClick={() => {
              Promise.resolve(signOut()).catch(() => undefined);
            }}
            size="icon"
            type="button"
            variant="ghost"
          >
            <LogOut aria-hidden="true" className="size-4" />
          </Button>
        </div>
      </div>
    </header>
  );
}
